const TIKTOK_ANALYTICS_OBJECT = "ttq";
const TIKTOK_SCRIPT_URL = "https://analytics.tiktok.com/i18n/pixel/events.js";
const TIKTOK_METHODS = [
  "page",
  "track",
  "identify",
  "instances",
  "debug",
  "on",
  "off",
  "once",
  "ready",
  "alias",
  "group",
  "enableCookie",
  "disableCookie",
  "holdConsent",
  "revokeConsent",
  "grantConsent",
];

const initializedPixels = new Set();

const addDeferredMethod = (queue, method) => {
  queue[method] = (...args) => queue.push([method, ...args]);
};

/**
 * 创建 TikTok 官方基础代码使用的命令队列。
 * SDK 下载完成前调用的 page/track 会先进入队列，加载后再按顺序执行。
 */
const getTikTokQueue = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }
  if (window.ttq) return window.ttq;

  const queue = [];
  window.TiktokAnalyticsObject = TIKTOK_ANALYTICS_OBJECT;
  window.ttq = queue;
  queue.methods = TIKTOK_METHODS;
  queue.setAndDefer = addDeferredMethod;
  TIKTOK_METHODS.forEach((method) => addDeferredMethod(queue, method));
  queue.instance = (pixelId) => {
    queue._i = queue._i || {};
    queue._i[pixelId] = queue._i[pixelId] || [];
    TIKTOK_METHODS.forEach((method) =>
      addDeferredMethod(queue._i[pixelId], method),
    );
    return queue._i[pixelId];
  };
  queue.load = (pixelId, options = {}) => {
    queue._i = queue._i || {};
    queue._i[pixelId] = queue._i[pixelId] || [];
    queue._i[pixelId]._u = TIKTOK_SCRIPT_URL;
    queue._t = queue._t || {};
    queue._t[pixelId] = Date.now();
    queue._o = queue._o || {};
    queue._o[pixelId] = options;

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = `${TIKTOK_SCRIPT_URL}?sdkid=${encodeURIComponent(
      pixelId,
    )}&lib=${TIKTOK_ANALYTICS_OBJECT}`;
    document.head.appendChild(script);
  };

  return queue;
};

/** 初始化指定 Pixel，并按需发送当前页面的 PageView。 */
export function initTikTokPixel(pixelId, { trackPageView = true } = {}) {
  const normalizedPixelId = String(pixelId || "").trim();
  if (!normalizedPixelId) return false;

  const queue = getTikTokQueue();
  if (!queue) return false;

  if (!initializedPixels.has(normalizedPixelId)) {
    queue.load?.(normalizedPixelId);
    initializedPixels.add(normalizedPixelId);
  }

  if (trackPageView) {
    const pixelInstance = queue.instance?.(normalizedPixelId);
    if (typeof pixelInstance?.page === "function") pixelInstance.page();
    else queue.page?.();
  }

  return true;
}

/**
 * 向指定 Pixel 发送事件。调用方只传通用业务参数，禁止传证件或申请人 PII。
 */
export function trackTikTokPixelEvent(pixelId, eventName, params = {}) {
  const normalizedPixelId = String(pixelId || "").trim();
  const normalizedEventName = String(eventName || "").trim();
  if (!normalizedPixelId || !normalizedEventName) return false;

  const queue = getTikTokQueue();
  const pixelInstance = queue?.instance?.(normalizedPixelId);
  if (typeof pixelInstance?.track === "function") {
    pixelInstance.track(normalizedEventName, params);
    return true;
  }
  if (typeof queue?.track === "function") {
    queue.track(normalizedEventName, params);
    return true;
  }
  return false;
}
